'use client';

import { c as _c } from "react/compiler-runtime";
import { FOCUS_RING, INTERACTIVE_SURFACE } from '@common/components/interaction';
import SvgChevrondown from '@common/icon/SvgChevrondown';
import SvgChevronright from '@common/icon/SvgChevronright';
import { cn } from '@core/utils';
import { useState } from 'react';
import { TERM_CATEGORY } from '@/api/types/parent-code';
import { useGlobalStore } from '@/stores/GlobalStore';
import ConfirmCheckIcon from './_assets/confirm-check.svg';
const THIRD_PARTY_TERMS = [{
  key: 'membershipTerms',
  label: '고향사랑e음 회원가입을 위한 개인정보 제공 동의',
  category: TERM_CATEGORY.GOHYANG_EUM_SIGNUP_THIRD_PARTY_AGREEMENT
}, {
  key: 'addressVerification',
  label: '주소지 확인을 위한 주민등록번호 제공 동의',
  category: TERM_CATEGORY.RESIDENT_REGISTRATION_PROVIDE_AGREEMENT
}, {
  key: 'shippingInfo',
  label: '답례품 배송 관련 개인정보 제3자 제공 동의',
  category: TERM_CATEGORY.GIFT_DELIVERY_THIRD_PARTY_AGREEMENT
}, {
  key: 'donorManagement',
  label: '개인정보 수집/이용/제공 동의(기부자 관리)',
  category: TERM_CATEGORY.PERSONAL_INFO_COLLECTION_AGREEMENT
}] as const;
const TERM_ITEMS = [{
  key: 'welloTerms',
  label: '웰로 고향사랑기부제 이용약관',
  category: TERM_CATEGORY.GOHYANG_LOVE_DONATION_TERMS
}, {
  key: 'serviceTerms',
  label: '웰로 이용약관',
  category: TERM_CATEGORY.SERVICE_TERMS
}, {
  key: 'thirdPartyInfo',
  label: '고향사랑e음 회원가입 및 연계를 위한 개인정보 제3자 제공 동의',
  children: THIRD_PARTY_TERMS
}, {
  key: 'voluntaryDonation',
  label: '공무 관련 강요 없이 자발적으로 기부합니다.',
  category: TERM_CATEGORY.TERMS_GOVERNMENT_PRESSURE,
  isDonationTerm: true
}, {
  key: 'guidanceNotice',
  label: '안내사항(환불 불가·세액공제 등)을 확인했습니다.',
  category: TERM_CATEGORY.TERMS_GUIDANCE_NOTICE,
  isDonationTerm: true
}, {
  key: 'giftInfo',
  label: '답례품 정보 확인 및 동의',
  category: TERM_CATEGORY.TERMS_GIFT_INFO_CHECK_AGREEMENT,
  isDonationTerm: true
}, {
  key: 'marketing',
  label: '마케팅 정보 수신 동의',
  category: TERM_CATEGORY.MARKETING_TERMS,
  isOptional: true
}] as const;
const THIRD_PARTY_KEYS = THIRD_PARTY_TERMS.map(({
  key
}) => key);

//* thirdPartyInfo는 하위 4건의 파생이라 자기 동의 상태를 갖지 않는다
const AGREEMENT_KEYS = [...TERM_ITEMS.flatMap(item => 'children' in item ? [] : [item.key]), ...THIRD_PARTY_KEYS];
const OPTIONAL_KEYS: readonly string[] = TERM_ITEMS.flatMap(item => 'isOptional' in item ? [item.key] : []);
const DONATION_KEYS: readonly string[] = TERM_ITEMS.flatMap(item => 'isDonationTerm' in item ? [item.key] : []);
type TermKey = (typeof AGREEMENT_KEYS)[number];
const EMPTY_AGREEMENTS = Object.fromEntries(AGREEMENT_KEYS.map(key => [key, false])) as Record<TermKey, boolean>;
interface DonationTermsOptions {
  hasMarketingTerm?: boolean;
  hasDonationTerms?: boolean;
}
export const useDonationTerms = t0 => {
  const $ = _c(29);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    hasMarketingTerm: t2,
    hasDonationTerms: t3
  } = t1;
  const hasMarketingTerm = t2 === undefined ? true : t2;
  const hasDonationTerms = t3 === undefined ? true : t3;
  const [agreements, setAgreements] = useState(EMPTY_AGREEMENTS);
  let agreeableKeys;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[2] !== agreements || $[3] !== hasDonationTerms || $[4] !== hasMarketingTerm) {
    let t9;
    if ($[11] !== hasDonationTerms) {
      t9 = key => hasDonationTerms || !DONATION_KEYS.includes(key);
      $[11] = hasDonationTerms;
      $[12] = t9;
    } else {
      t9 = $[12];
    }
    const activeKeys = AGREEMENT_KEYS.filter(t9);
    const requiredKeys = activeKeys.filter(_temp);
    agreeableKeys = hasMarketingTerm ? activeKeys : requiredKeys;
    t4 = agreements;
    t5 = hasMarketingTerm;
    t6 = hasDonationTerms;
    let t10;
    if ($[13] !== agreements) {
      t10 = key_1 => agreements[key_1];
      $[13] = agreements;
      $[14] = t10;
    } else {
      t10 = $[14];
    }
    t7 = requiredKeys.every(t10);
    let t11;
    if ($[15] !== agreements) {
      t11 = key_2 => agreements[key_2];
      $[15] = agreements;
      $[16] = t11;
    } else {
      t11 = $[16];
    }
    t8 = agreeableKeys.every(t11);
    $[2] = agreements;
    $[3] = hasDonationTerms;
    $[4] = hasMarketingTerm;
    $[5] = agreeableKeys;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    agreeableKeys = $[5];
    t4 = $[6];
    t5 = $[7];
    t6 = $[8];
    t7 = $[9];
    t8 = $[10];
  }
  let t10;
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = (key_3, checked) => setAgreements(previous => ({
      ...previous,
      [key_3]: checked
    }));
    t10 = checked_0 => setAgreements(previous_0 => ({
      ...previous_0,
      ...Object.fromEntries(THIRD_PARTY_KEYS.map(key_4 => [key_4, checked_0]))
    }));
    $[17] = t10;
    $[18] = t9;
  } else {
    t10 = $[17];
    t9 = $[18];
  }
  let t11;
  if ($[19] !== agreeableKeys) {
    t11 = () => setAgreements(previous_1 => ({
      ...previous_1,
      ...Object.fromEntries(agreeableKeys.map(_temp2))
    }));
    $[19] = agreeableKeys;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => setAgreements(EMPTY_AGREEMENTS);
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t11 || $[23] !== t4 || $[24] !== t5 || $[25] !== t6 || $[26] !== t7 || $[27] !== t8) {
    t13 = {
      agreements: t4,
      hasMarketingTerm: t5,
      hasDonationTerms: t6,
      isAllRequiredAgreed: t7,
      isAllAgreed: t8,
      setAgreement: t9,
      setThirdParty: t10,
      agreeAll: t11,
      reset: t12
    };
    $[22] = t11;
    $[23] = t4;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  return t13;
};
type DonationTermsState = ReturnType<typeof useDonationTerms>;
const TermRow = t0 => {
  const $ = _c(37);
  const {
    label,
    isOptional,
    checked,
    isNested,
    onCheckedChange,
    onOpenDetail,
    expandState
  } = t0;
  const t1 = isNested && "pl-16";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("-ml-4 flex items-start", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn("shrink-0 rounded-xsmall p-4", FOCUS_RING);
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== checked || $[4] !== onCheckedChange) {
    t4 = () => onCheckedChange(!checked);
    $[3] = checked;
    $[4] = onCheckedChange;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = checked ? "text-Gray-black kakaobank:text-O-400" : "text-Gray-300";
  let t6;
  if ($[6] !== t5) {
    t6 = cn("size-20 transition-colors duration-150 motion-reduce:transition-none", t5);
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t6) {
    t7 = <ConfirmCheckIcon className={t6} />;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== checked || $[11] !== label || $[12] !== t4 || $[13] !== t7) {
    t8 = <button type="button" role="checkbox" aria-checked={checked} aria-label={label} className={t3} onClick={t4}>{t7}</button>;
    $[10] = checked;
    $[11] = label;
    $[12] = t4;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const t9 = `${label} 자세히 보기`;
  const t10 = expandState ? expandState.isExpanded : undefined;
  let t11;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = cn("flex flex-1 items-baseline gap-4 rounded-xsmall py-4 text-left", INTERACTIVE_SURFACE);
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== expandState || $[17] !== onOpenDetail) {
    t12 = () => expandState ? expandState.onToggle() : onOpenDetail?.();
    $[16] = expandState;
    $[17] = onOpenDetail;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== isNested || $[20] !== isOptional) {
    t13 = !isNested && <span className="shrink-0 font-400 text-Gray-700 typo-caption1">{isOptional ? "[\uC120\uD0DD]" : "[\uD544\uC218]"}</span>;
    $[19] = isNested;
    $[20] = isOptional;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== label) {
    t14 = <span className="flex-1 break-keep font-400 text-Gray-700 typo-body4">{label}</span>;
    $[22] = label;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== expandState) {
    t15 = expandState ? <SvgChevrondown className={cn("mx-4 mt-4 size-12 shrink-0 self-start text-Gray-550 transition-transform duration-200 motion-reduce:transition-none", expandState.isExpanded && "rotate-180")} /> : <SvgChevronright className="mx-4 mt-4 size-12 shrink-0 self-start text-Gray-550" />;
    $[24] = expandState;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t10 || $[27] !== t12 || $[28] !== t13 || $[29] !== t14 || $[30] !== t15 || $[31] !== t9) {
    t16 = <button type="button" aria-label={t9} aria-expanded={t10} className={t11} onClick={t12}>{t13}{t14}{t15}</button>;
    $[26] = t10;
    $[27] = t12;
    $[28] = t13;
    $[29] = t14;
    $[30] = t15;
    $[31] = t9;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t16 || $[34] !== t2 || $[35] !== t8) {
    t17 = <li className={t2}>{t8}{t16}</li>;
    $[33] = t16;
    $[34] = t2;
    $[35] = t8;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  return t17;
};
export const DonationTermsList = t0 => {
  const $ = _c(38);
  const {
    agreements,
    hasMarketingTerm,
    hasDonationTerms,
    isAllAgreed,
    setAgreement,
    setThirdParty,
    agreeAll,
    reset,
    className,
    isThirdPartyExpandedByDefault: t1
  } = t0;
  const isThirdPartyExpandedByDefault = t1 === undefined ? false : t1;
  const [isThirdPartyExpanded, setIsThirdPartyExpanded] = useState(isThirdPartyExpandedByDefault);
  const setTermsDrawerType = useGlobalStore(_temp3);
  let t2;
  if ($[0] !== className) {
    t2 = cn("flex flex-col gap-16", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <h2 className="font-700 text-Gray-black typo-body2">고향사랑기부 서비스 이용에<br />필요한 동의만 골랐어요</h2>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = isAllAgreed ? "bg-Gray-black text-Gray-white kakaobank:bg-O-400" : "bg-Gray-25 text-Gray-black";
  let t5;
  if ($[3] !== t4) {
    t5 = cn("flex w-full items-center gap-6 rounded-small p-12", "transition duration-150 motion-reduce:transition-none", "hover:opacity-80 active:opacity-80", FOCUS_RING, t4);
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== agreeAll || $[6] !== isAllAgreed || $[7] !== reset) {
    t6 = () => isAllAgreed ? reset() : agreeAll();
    $[5] = agreeAll;
    $[6] = isAllAgreed;
    $[7] = reset;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ConfirmCheckIcon className="size-24 shrink-0" />;
    t8 = <span className="font-600 typo-body3">모두 동의</span>;
    $[9] = t7;
    $[10] = t8;
  } else {
    t7 = $[9];
    t8 = $[10];
  }
  let t9;
  if ($[11] !== isAllAgreed || $[12] !== t5 || $[13] !== t6) {
    t9 = <button type="button" role="checkbox" aria-checked={isAllAgreed} className={t5} onClick={t6}>{t7}{t8}</button>;
    $[11] = isAllAgreed;
    $[12] = t5;
    $[13] = t6;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== agreements || $[16] !== hasDonationTerms || $[17] !== hasMarketingTerm || $[18] !== isThirdPartyExpanded || $[19] !== setAgreement || $[20] !== setTermsDrawerType || $[21] !== setThirdParty) {
    let t11;
    if ($[23] !== hasDonationTerms || $[24] !== hasMarketingTerm) {
      t11 = item => (hasMarketingTerm || !("isOptional" in item)) && (hasDonationTerms || !("isDonationTerm" in item));
      $[23] = hasDonationTerms;
      $[24] = hasMarketingTerm;
      $[25] = t11;
    } else {
      t11 = $[25];
    }
    let t12;
    if ($[26] !== agreements || $[27] !== isThirdPartyExpanded || $[28] !== setAgreement || $[29] !== setTermsDrawerType || $[30] !== setThirdParty) {
      t12 = item_0 => "children" in item_0 ? <li key={item_0.key}><ul className="flex flex-col"><TermRow label={item_0.label} checked={THIRD_PARTY_KEYS.every(key => agreements[key])} onCheckedChange={setThirdParty} expandState={{
            isExpanded: isThirdPartyExpanded,
            onToggle: () => setIsThirdPartyExpanded(_temp4)
          }} /><li className={cn("grid transition-[grid-template-rows] duration-300 ease-in-out motion-reduce:transition-none", isThirdPartyExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]")}><ul className="flex flex-col overflow-hidden" inert={!isThirdPartyExpanded || undefined}>{item_0.children.map(child => <TermRow key={child.key} isNested={true} label={child.label} checked={agreements[child.key]} onCheckedChange={checked => setAgreement(child.key, checked)} onOpenDetail={() => setTermsDrawerType(child.category)} />)}</ul></li></ul></li> : <TermRow key={item_0.key} label={item_0.label} isOptional={"isOptional" in item_0 ? item_0.isOptional : undefined} checked={agreements[item_0.key]} onCheckedChange={checked_0 => setAgreement(item_0.key, checked_0)} onOpenDetail={() => setTermsDrawerType(item_0.category)} />;
      $[26] = agreements;
      $[27] = isThirdPartyExpanded;
      $[28] = setAgreement;
      $[29] = setTermsDrawerType;
      $[30] = setThirdParty;
      $[31] = t12;
    } else {
      t12 = $[31];
    }
    t10 = TERM_ITEMS.filter(t11).map(t12);
    $[15] = agreements;
    $[16] = hasDonationTerms;
    $[17] = hasMarketingTerm;
    $[18] = isThirdPartyExpanded;
    $[19] = setAgreement;
    $[20] = setTermsDrawerType;
    $[21] = setThirdParty;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[32] !== t10) {
    t11 = <ul className="-my-4 flex flex-col">{t10}</ul>;
    $[32] = t10;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== t11 || $[35] !== t2 || $[36] !== t9) {
    t12 = <section className={t2}>{t3}{t9}{t11}</section>;
    $[34] = t11;
    $[35] = t2;
    $[36] = t9;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  return t12;
};
function _temp(key_0) {
  return !OPTIONAL_KEYS.includes(key_0);
}
function _temp2(key_5) {
  return [key_5, true];
}
function _temp3(state) {
  return state.setTermsDrawerType;
}
function _temp4(open) {
  return !open;
}